/**
 * Utility functions for handling and displaying user-friendly error messages
 * Converts system/OS errors and network errors to human-readable messages
 */

/**
 * Maps common error types to user-friendly messages
 */
const ERROR_MESSAGE_MAP = {
  // Network connectivity errors
  'NetworkError': 'Unable to connect to the internet. Please check your connection and try again.',
  'net::ERR_NETWORK_CHANGED': 'Your network connection changed. Please try again.',
  'net::ERR_INTERNET_DISCONNECTED': 'No internet connection. Please check your network and try again.',
  'net::ERR_CONNECTION_REFUSED': 'Unable to connect to our servers. Please try again in a moment.',
  'net::ERR_CONNECTION_TIMED_OUT': 'Connection timed out. Please check your internet connection and try again.',
  'net::ERR_NAME_NOT_RESOLVED': 'Unable to reach our servers. Please check your internet connection.',
  'ENOTFOUND': 'Unable to connect to our servers. Please check your internet connection.',
  'ECONNREFUSED': 'Unable to connect to our servers. Please try again in a moment.',
  'ETIMEDOUT': 'Connection timed out. Please check your internet connection and try again.',
  'ECONNRESET': 'Connection was interrupted. Please try again.',
  
  // HTTP status errors
  '400': 'Invalid request. Please check your input and try again.',
  '401': 'Authentication required. Please sign in again.',
  '403': 'Access denied. You don\'t have permission for this action.',
  '404': 'Content not found. It may have been moved or deleted.',
  '408': 'Request timed out. Please try again.',
  '429': 'Too many requests. Please wait a moment and try again.',
  '500': 'Server error. We\'re working on fixing this. Please try again later.',
  '502': 'Service temporarily unavailable. Please try again in a moment.',
  '503': 'Service temporarily unavailable. Please try again in a moment.',
  '504': 'Request timed out. Please try again.',
  
  // Upload/file errors
  'Upload failed': 'Failed to upload file. Please check your internet connection and try again.',
  'File too large': 'File is too large. Please choose a smaller file.',
  'Invalid file type': 'File type not supported. Please choose a different file.',
  
  // WebSocket/real-time connection errors
  'WebSocket connection failed': 'Unable to establish real-time connection. Some features may be limited.',
  'connect_error': 'Connection to live updates failed. Please refresh the page if you experience issues.',
  
  // Generic fallbacks
  'fetch': 'Network request failed. Please check your connection and try again.',
  'Failed to fetch': 'Network request failed. Please check your connection and try again.',
  'Load failed': 'Failed to load content. Please check your connection and try again.',
};

/**
 * Gets a user-friendly error message from a system/network error
 * @param {Error|string|object} error - The error to process
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyErrorMessage(error) {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  let errorString = '';
  let errorMessage = '';
  let statusCode = null;

  // Extract error information from different error types
  if (typeof error === 'string') {
    errorString = error;
    errorMessage = error;
  } else if (error instanceof Error) {
    errorString = error.message || error.toString();
    errorMessage = error.message || '';
  } else if (error.message) {
    errorString = error.message;
    errorMessage = error.message;
  } else if (error.statusText) {
    errorString = error.statusText;
    errorMessage = error.statusText;
  } else {
    errorString = JSON.stringify(error);
    errorMessage = 'An unexpected error occurred';
  }

  // Extract status code if available
  if (error.status) {
    statusCode = error.status.toString();
  } else if (error.statusCode) {
    statusCode = error.statusCode.toString();
  }

  // Check for specific error patterns
  for (const [pattern, message] of Object.entries(ERROR_MESSAGE_MAP)) {
    if (errorString.includes(pattern) || errorMessage.includes(pattern)) {
      return message;
    }
  }

  // Check status code
  if (statusCode && ERROR_MESSAGE_MAP[statusCode]) {
    return ERROR_MESSAGE_MAP[statusCode];
  }

  // Handle specific common patterns
  if (errorString.includes('socket') || errorString.includes('Socket')) {
    return 'Connection failed. Please check your internet connection and try again.';
  }

  if (errorString.includes('timeout') || errorString.includes('Timeout')) {
    return 'Request timed out. Please check your internet connection and try again.';
  }

  if (errorString.includes('network') || errorString.includes('Network')) {
    return 'Network error. Please check your internet connection and try again.';
  }

  if (errorString.includes('CORS')) {
    return 'Connection blocked by security settings. Please refresh the page and try again.';
  }

  if (errorString.includes('JSON') || errorString.includes('parse')) {
    return 'Received invalid response from server. Please try again.';
  }

  // Return a generic friendly message for unknown errors
  return 'Something went wrong. Please check your internet connection and try again.';
}

/**
 * Creates a user-friendly toast notification for errors
 * @param {object} toast - PrimeVue toast instance
 * @param {Error|string|object} error - The error to display
 * @param {string} summary - Optional custom summary/title
 * @param {number} life - Toast display duration in milliseconds (default: 5000)
 */
export function showErrorToast(toast, error, summary = 'Error', life = 5000) {
  const detail = getUserFriendlyErrorMessage(error);
  
  toast.add({
    severity: 'error',
    summary,
    detail,
    life,
  });
}

/**
 * Handles network request errors with automatic retry logic
 * @param {function} requestFunction - Function that makes the network request
 * @param {object} options - Options for retry logic
 * @param {number} options.maxRetries - Maximum number of retries (default: 2)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
 * @param {function} options.shouldRetry - Function to determine if error should trigger retry
 * @returns {Promise} Result of the request or throws user-friendly error
 */
export async function handleNetworkRequest(requestFunction, options = /** @type {{ maxRetries: number; retryDelay: number; shouldRetry: (error:any)=>boolean }} */({ maxRetries: 2, retryDelay: 1000, shouldRetry: (e)=>true })) {
  const { 
    maxRetries = 2, 
    retryDelay = 1000,
    shouldRetry = (error) => {
      // Retry on network errors, timeouts, and server errors (5xx)
      const errorString = error.toString().toLowerCase();
      return errorString.includes('network') || 
             errorString.includes('timeout') || 
             errorString.includes('fetch') ||
             (error.status >= 500 && error.status < 600);
    }
  } = options;

  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFunction();
    } catch (error) {
      lastError = error;
      
      // Don't retry on the last attempt or if shouldn't retry
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw new Error(getUserFriendlyErrorMessage(error));
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  throw new Error(getUserFriendlyErrorMessage(lastError));
}

/**
 * Wraps fetch requests with better error handling
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response with improved error handling
 */
export async function fetchWithErrorHandling(url, options = {}) {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      // Create a more informative error for HTTP errors
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      /** @type {any} */(error).status = response.status;
      /** @type {any} */(error).statusText = response.statusText;
      throw error;
    }
    
    return response;
  } catch (error) {
    // Re-throw with user-friendly message
    const friendlyMessage = getUserFriendlyErrorMessage(error);
    const newError = new Error(friendlyMessage);
    /** @type {any} */(newError).originalError = error;
    throw newError;
  }
}

export default {
  getUserFriendlyErrorMessage,
  showErrorToast,
  handleNetworkRequest,
  fetchWithErrorHandling,
};
